import { Link } from 'react-router-dom';
import { AlertTriangle, Phone, Shield, FileText, Scale, Award, Smartphone } from 'lucide-react';
import AppStoreBadges from './AppStoreBadges';

export default function GlobalFooter() {
  return (
    <footer className="bg-gray-50 border-t border-border mt-auto">
      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Integrity Banner */}
        <div className="mb-4 p-5 bg-[#1A1A1A] rounded-xl text-white">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center flex-shrink-0">
              <Award className="w-5 h-5 text-white" />
            </div>
            <div>
              <h4 className="font-semibold text-lg mb-1">100% Independent. 100% Clinical.</h4>
              <p className="text-sm text-gray-300 leading-relaxed">
                Vitala was founded by healthcare management professionals who saw a gap in modern wellness: 
                people are over-supplemented but under-nourished. We do not sell products. We do not use affiliate links. 
                We do not accept brand sponsorships. Our only goal is to bridge the nutrient gap created by modern 
                lifestyles and depleted food systems with unbiased, AI-driven data.
              </p>
            </div>
          </div>
        </div>

        {/* Medical Disclaimer Banner */}
        <div className="flex items-start gap-3 mb-4 p-4 bg-amber-50 rounded-lg border border-amber-200">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-amber-800">
            <p className="mb-0">
              <strong>Vitala.me</strong> is an AI-powered informational tool. We are not doctors. 
              Our suggestions are <strong>not FDA-evaluated</strong> and do not constitute medical advice.{' '}
              <Link to="/disclaimer" className="text-amber-700 underline hover:text-amber-900 font-medium">
                Read our Medical Disclaimer
              </Link>
            </p>
          </div>
        </div>
        
        {/* App store badges — site-wide download CTA */}
        <div className="flex flex-col items-center gap-2 mb-5">
          <p className="text-xs font-medium text-muted-foreground" data-testid="global-footer-download-label">
            Download the Vitala app
          </p>
          <AppStoreBadges variant="both" size="md" align="center" />
        </div>

        {/* Footer Links */}
        <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm text-muted-foreground">
          <Link to="/app" className="hover:text-primary transition-colors flex items-center gap-1.5">
            <Smartphone className="w-3.5 h-3.5" />
            Get the App
          </Link>
          <Link to="/privacy" className="hover:text-primary transition-colors flex items-center gap-1.5">
            <Shield className="w-3.5 h-3.5" />
            Privacy Policy
          </Link>
          <Link to="/terms" className="hover:text-primary transition-colors flex items-center gap-1.5">
            <FileText className="w-3.5 h-3.5" />
            Terms & Conditions
          </Link>
          <Link to="/disclaimer" className="hover:text-primary transition-colors flex items-center gap-1.5">
            <Scale className="w-3.5 h-3.5" />
            Medical Disclaimer
          </Link>
          <Link to="/guide" className="hover:text-primary transition-colors flex items-center gap-1.5">
            <Award className="w-3.5 h-3.5" />
            Buyer's Safety Guide
          </Link>
          <a 
            href="https://en.wikipedia.org/wiki/List_of_emergency_telephone_numbers" 
            target="_blank" 
            rel="noopener noreferrer"
            className="hover:text-red-600 transition-colors flex items-center gap-1.5 text-red-500"
          >
            <Phone className="w-3.5 h-3.5" />
            Emergency Numbers
          </a>
        </div>
        
        {/* Copyright */}
        <p className="text-center text-xs text-muted-foreground mt-4">
          © {new Date().getFullYear()} Medpax, LLC. Vitala™ is a trademark of Medpax, LLC. All rights reserved.
        </p>
      </div>
    </footer>
  );
}
